import React from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeProvider";
import PublicLayout from "@/components/public/PublicLayout";
import ProtectedRoute from "@/components/dashboard/ProtectedRoute";

import Home from "@/pages/public/Home";
import Services from "@/pages/public/Services";
import InfoPage from "@/pages/public/InfoPage";
import FAQ from "@/pages/public/FAQ";
import Contact from "@/pages/public/Contact";
import Team from "@/pages/public/Team";
import About from "@/pages/public/About";
import HowItWorks from "@/pages/public/HowItWorks";
import RequestWizard from "@/pages/public/RequestWizard";
import PublicAvailability from "@/pages/public/PublicAvailability";

import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import AuthCallback from "@/pages/auth/AuthCallback";

import DoctorLayout from "@/pages/doctor/DoctorLayout";
import DoctorHome from "@/pages/doctor/DoctorHome";
import DoctorBooking from "@/pages/doctor/DoctorBooking";
import DoctorRequests from "@/pages/doctor/DoctorRequests";
import Profile from "@/pages/doctor/Profile";

import SecretaryLayout from "@/pages/secretary/SecretaryLayout";
import SecretaryHome from "@/pages/secretary/SecretaryHome";
import SecretaryMissions from "@/pages/secretary/SecretaryMissions";
import Availability from "@/pages/secretary/Availability";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminRequests from "@/pages/admin/AdminRequests";
import AdminProjects from "@/pages/admin/AdminProjects";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminClients from "@/pages/admin/AdminClients";
import AdminTeam from "@/pages/admin/AdminTeam";
import AdminPlanning from "@/pages/admin/AdminPlanning";
import AdminCallbacks from "@/pages/admin/AdminCallbacks";
import AdminServices from "@/pages/admin/AdminServices";
import AdminSite from "@/pages/admin/AdminSite";
import AdminDesign from "@/pages/admin/AdminDesign";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminMedia from "@/pages/admin/AdminMedia";
import AdminLogo from "@/pages/admin/AdminLogo";
import AdminPages from "@/pages/admin/AdminPages";
import AdminBackups from "@/pages/admin/AdminBackups";
import AdminIntegrations from "@/pages/admin/AdminIntegrations";
import AdminTrash from "@/pages/admin/AdminTrash";
import StaffCalendar from "@/pages/secretary/StaffCalendar";
import PageGuard from "@/components/public/PageGuard";
import Messages from "@/components/shared/Messages";

const Pub = (Cmp) => <PublicLayout><PageGuard><Cmp /></PageGuard></PublicLayout>;
const PubSlug = (Cmp, slug) => <PublicLayout><PageGuard slug={slug}><Cmp slug={slug} /></PageGuard></PublicLayout>;

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={Pub(Home)} />
      <Route path="/services" element={Pub(Services)} />
      <Route path="/comment-ca-marche" element={Pub(HowItWorks)} />
      <Route path="/pour-les-medecins" element={PubSlug(InfoPage, "pour-les-medecins")} />
      <Route path="/pour-les-cabinets" element={PubSlug(InfoPage, "pour-les-cabinets")} />
      <Route path="/ouverture-reprise-cabinet" element={PubSlug(InfoPage, "ouverture-reprise-cabinet")} />
      <Route path="/installation-suisse" element={PubSlug(InfoPage, "installation-suisse")} />
      <Route path="/office-manager" element={PubSlug(InfoPage, "office-manager")} />
      <Route path="/a-propos" element={Pub(About)} />
      <Route path="/equipe" element={Pub(Team)} />
      <Route path="/faq" element={Pub(FAQ)} />
      <Route path="/contact" element={Pub(Contact)} />
      <Route path="/disponibilites" element={Pub(PublicAvailability)} />
      <Route path="/demande" element={<PublicLayout><RequestWizard /></PublicLayout>} />

      <Route path="/connexion" element={<Login />} />
      <Route path="/inscription" element={<Register />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      <Route path="/espace" element={<ProtectedRoute><DoctorLayout /></ProtectedRoute>}>
        <Route index element={<DoctorHome />} />
        <Route path="reserver" element={<DoctorBooking />} />
        <Route path="demandes" element={<DoctorRequests />} />
        <Route path="messages" element={<Messages />} />
        <Route path="profil" element={<Profile />} />
      </Route>

      <Route path="/secretaire" element={<ProtectedRoute roles={["secretary"]}><SecretaryLayout /></ProtectedRoute>}>
        <Route index element={<SecretaryHome />} />
        <Route path="missions" element={<SecretaryMissions />} />
        <Route path="disponibilites" element={<Availability />} />
        <Route path="calendrier" element={<StaffCalendar />} />
      </Route>

      <Route path="/admin" element={<ProtectedRoute perm="manage_requests"><AdminLayout /></ProtectedRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="demandes" element={<AdminRequests />} />
        <Route path="projets" element={<AdminProjects />} />
        <Route path="prospects" element={<AdminLeads />} />
        <Route path="clients" element={<AdminClients />} />
        <Route path="equipe" element={<AdminTeam />} />
        <Route path="planning" element={<AdminPlanning />} />
        <Route path="messages" element={<Messages admin />} />
        <Route path="rappels-urgents" element={<AdminCallbacks />} />
        <Route path="services" element={<AdminServices />} />
        <Route path="site" element={<AdminSite />} />
        <Route path="site/medias" element={<AdminMedia />} />
        <Route path="site/pages" element={<AdminPages />} />
        <Route path="design" element={<AdminDesign />} />
        <Route path="design/logo" element={<AdminLogo />} />
        <Route path="sauvegardes" element={<AdminBackups />} />
        <Route path="integrations" element={<AdminIntegrations />} />
        <Route path="corbeille" element={<AdminTrash />} />
        <Route path="audit" element={<AdminAudit />} />
      </Route>

      <Route path="*" element={Pub(Home)} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
